/**
 * FeaturedStoryStrip — surfaces the newest employer success story near the
 * signup CTA. Renders nothing when no real (non-placeholder) customer story
 * has been published yet.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Quote, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export function FeaturedStoryStrip({ compact = false }) {
    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/content/success-stories/featured")
            .then((r) => { if (mounted) setStory(r.data?.story || null); })
            .catch(() => { if (mounted) setStory(null); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    if (loading || !story) return null;

    return (
        <aside
            data-testid="employer-featured-story"
            className={`card-soft border-emerald-500/40 bg-gradient-to-br from-emerald-500/5 via-background to-sky-500/5 ${compact ? "p-5" : "p-6"}`}
        >
            <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                    <Sparkles size={11} /> Latest success story
                </div>
                {story.time_to_shortlist && (
                    <span className="text-[11px] text-muted-foreground font-medium">
                        Shortlist in {story.time_to_shortlist}
                    </span>
                )}
            </div>
            <div className="mt-3 flex items-start gap-3">
                <Quote size={18} className="text-emerald-500 shrink-0 mt-1" />
                <div>
                    <p className="text-sm text-foreground leading-relaxed" data-testid="employer-featured-story-quote">
                        &ldquo;{story.quote}&rdquo;
                    </p>
                    <div className="mt-2 text-[11px] text-muted-foreground">
                        {story.author_name ? <><b className="text-foreground">{story.author_name}</b> · </> : null}
                        {story.role_hired}{story.company_type ? <> · {story.company_type}</> : null}
                    </div>
                </div>
            </div>
            <div className="mt-3">
                <Link
                    to="/success-stories"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                    data-testid="employer-featured-story-link"
                >
                    See every success story <ArrowRight size={11} />
                </Link>
            </div>
        </aside>
    );
}

export default FeaturedStoryStrip;
